import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useEffect} from 'react';

/**
 * Plugin to sanitize pasted content in comment boxes by stripping
 * disallowed formatting (eg. justify alignment, indent, etc).
 * This intercepts paste at the DOM level and strips formatting from the HTML.
 */
export const SanitizeCommentPastePlugin = (): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const rootElement = editor.getRootElement();

    if (!rootElement) {
      return;
    }

    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      // Get HTML content
      const html = clipboardData.getData('text/html');

      if (!html) {
        return; // Let Lexical handle plain text normally
      }

      // Parse and clean the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Remove all style attributes that might contain text-align
      doc.querySelectorAll('[style]').forEach((el) => {
        const style = (el as HTMLElement).style;
        // Remove text-align specifically
        style.textAlign = '';
        // If style is now empty, remove the attribute
        if (!style.cssText) {
          el.removeAttribute('style');
        }
      });

      // Remove align attributes
      doc.querySelectorAll('[align]').forEach((el) => {
        el.removeAttribute('align');
      });

      // Get the cleaned HTML
      const cleanedHtml = doc.body.innerHTML;

      // Create a new DataTransfer object with the cleaned HTML
      try {
        // Modify the clipboardData
        const dt = new DataTransfer();
        dt.setData('text/html', cleanedHtml);
        dt.setData('text/plain', clipboardData.getData('text/plain'));

        // Replace the event's clipboardData
        Object.defineProperty(event, 'clipboardData', {
          value: dt,
          writable: false,
        });
      } catch (e) {
        console.error('Failed to modify clipboard data:', e);
      }
    };

    // Use capture phase to intercept before Lexical's handlers
    rootElement.addEventListener('paste', handlePaste, true);

    return () => {
      rootElement.removeEventListener('paste', handlePaste, true);
    };
  }, [editor]);

  return null;
};
